/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { uniq } from 'lodash';
import { DEFAULT_MAX_SIZE_KILO_BYTES } from 'libs/utils/metadata';

const DEFAULT_MIME_FOR_EXTENSION = {
    'ttf': 'font/ttf'
};

const __vue_script__ = {
    name: 'FileDropper',

    props: {
        label: {
            type: String,
            default: null
        },

        hint: {
            type: String,
            default: null
        },

        supportedMimes: {
            type: Array,
            default: () => []
        },

        maxSize: {
            type: Number,
            default: DEFAULT_MAX_SIZE_KILO_BYTES
        },

        attachmentSpecs: {
            type: Object,
            default: null
        },

        multiple: {
            type: Boolean,
            default: true
        }
    },

    data() {
        const supportedMimes = uniq([
            ...(this.supportedMimes ? this.supportedMimes : []),
            ...(this.attachmentSpecs?.allowed_content_types ? this.attachmentSpecs.allowed_content_types : []),
        ]);

        const allTypes = this.$utils.assets.humanReadableMime(supportedMimes);
        const types = this.$utils.strings.humanJoin(allTypes || [], this.$i18n.t('base.and'));
        return {
            types,
            mimes: supportedMimes,
            over: false
        };
    },

    computed: {
        /** @returns {string} */
        accept() {
            return this.mimes.length ? this.mimes.join(', ') : null;
        },

        /** @returns {string} */
        cssClasses() {
            return this.over ? 'active' : '';
        },

        validationParams() {
            const validation = this.attachmentSpecs?.uploader_settings?.validation || {};
            return {
                maxSize: validation.max_file_size_kilo_bytes ?? this.maxSize ?? DEFAULT_MAX_SIZE_KILO_BYTES,
                mimeRules: validation.mime_type_rules || {},
            };
        },

        humanReadableConstraints() {
            return [this.fileTypeAndSizeHint, ...this.postProcessingHint];
        },

        mimeTypesByFileSize() {
            const { mimeRules, maxSize } = this.validationParams;
            const sizeMap = {};

            for (let mime of this.mimes) {
                const allowedSize = mimeRules[mime]?.max_file_size_kilo_bytes || maxSize;

                if (!sizeMap[allowedSize]) {
                    sizeMap[allowedSize] = [];
                }
                sizeMap[allowedSize].push(mime);
            }

            return Object.entries(sizeMap)
                .sort((a, b) => b[1].length - a[1].length);
        },

        fileTypeAndSizeHint() {
            const hasManySizes = this.mimeTypesByFileSize.length > 1;
            const lastIndex = this.mimeTypesByFileSize.length - 1;
            let hint = '';

            for (let index = 0; index < this.mimeTypesByFileSize.length; index++) {
                const [size, mimes] = this.mimeTypesByFileSize[index];

                const exts = this.$utils.assets.humanReadableMime(mimes);
                const joinedExts = this.$utils.strings.humanJoin(
                    exts,
                    hasManySizes ? '' : this.$i18n.t('base.and')
                );
                const sizeText = this.$utils.numbers.humanFileSize(size * 1000, true);

                let key = 'constraints_type_and_size';
                if (hasManySizes) {
                    let part = index === lastIndex ? 3 : 2;
                    part = index === 0 ? 1 : part;
                    key = `constraints_type_and_size_multi_part_${part}`;
                }

                hint += this.$tc(`media.uploads.${key}`, mimes.length, [
                    joinedExts,
                    sizeText
                ]);
            }

            return hint;
        },

        postProcessingHint() {
            const postProcessing = this.attachmentSpecs?.postprocessing || [];

            const hints = [];
            postProcessing.forEach(proc => {
                if (proc.type === 'image_processing' && proc.params?.dimensions) {
                    const { width, height } = proc.params.dimensions;
                    const forExts = this.$utils.strings.humanJoin(
                        this.$utils.assets.humanReadableMime(proc.for_content_types || []),
                        this.$i18n.t('base.and')
                    );
                    hints.push(this.$tc('media.uploads.constraints_image_max_dimensions_both', 1, [forExts, width, height]));
                }
            });

            return hints;
        }
    },

    methods: {
        /** @param {DragEvent} event */
        onDrop(event) {
            this.changed(event.dataTransfer.files);
        },

        onDragOver() {
            this.over = true;
        },

        selectFiles() {
            // @ts-ignore
            this.$refs.input.click();
        },

        /** @param {FileList} files */
        changed(files) {
            const dt = new DataTransfer();

            for (const file of Array.from(files)) {
                if (this.isFileValid(file)) {
                    dt.items.add(file);
                }
            }

            files = dt.files;

            if (files.length) {
                this.$emit('input', files);
            } else {
                this.$notify({
                    group: 'flashes',
                    text: this.$i18n.t('general.forms.no_valid_files').toString(),
                    type: 'error'
                });
            }
        },

        clear() {
            this.$refs.input.value = '';
        },

        getFileMime(file) {
            if (file.type) return file.type;
            // Some file types are not properly detected by the OS/browser
            const extension = file.name?.split('.').pop();
            return DEFAULT_MIME_FOR_EXTENSION[extension] || null;
        },

        getAllowedSize(mimeType) {
            const { maxSize, mimeRules } = this.validationParams;
            return mimeRules[mimeType]?.max_file_size_kilo_bytes || maxSize;
        },

        /**
         * Checks the file validity
         *
         * @param {File} file the file to check
         *
         * @returns {boolean} true if the file is valid
         */
        isFileValid(file) {
            const type = this.getFileMime(file);

            if (this.accept && !this.mimes?.includes(type)) {
                console.error('[FileDropper] File type not accepted', { type, accepted: this.mimes });
                this.$notify({
                    group: 'flashes',
                    text: this.$i18n.t('media.uploads.file_type_not_accepted').toString(),
                    type: 'error'
                });
                return false;
            }

            const allowedSize = this.getAllowedSize(type);
            if (file.size / 1000 > allowedSize) {
                this.fileTooBigHandler(file.size, allowedSize);
                return false;
            }

            // @deprecated, use attachmentSpecs instead
            if (this.maxSize && file.size > this.maxSize) {
                this.fileTooBigHandler(file.size, this.maxSize);
                return false;
            }

            return true;
        },

        fileTooBigHandler(size, allowedSize) {
            console.error('[FileDropper] File size is too big', { size, allowedSize });
            this.$notify({
                group: 'flashes',
                text: this.$i18n.t('media.uploads.file_too_big', this.$utils.numbers.humanFileSize(allowedSize * 1000, true)).toString(),
                type: 'error'
            });
            return false;
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"file-dropper"},[(_vm.label)?_c('label',[_vm._v(_vm._s(_vm.label))]):_vm._e(),_c('div',{staticClass:"allowed-factors"},_vm._l((_vm.humanReadableConstraints),function(constraint){return _c('div',{key:constraint},[_vm._v(_vm._s(constraint))])}),0),_c('div',{staticClass:"drop-zone",class:_vm.cssClasses,on:{"drop":function($event){$event.preventDefault();return _vm.onDrop($event)},"dragover":function($event){$event.preventDefault();return _vm.onDragOver($event)},"dragleave":function($event){_vm.over = false},"dragenter":function($event){$event.preventDefault();}}},[_c('i',{staticClass:"ic-upload"}),_c('i18n',{attrs:{"path":"general.forms.drag_and_drop","tag":"p"}},[_c('a',{staticClass:"has-action",on:{"click":_vm.selectFiles}},[_vm._v(_vm._s(_vm.$t('general.forms.drag_and_drop_action')))])]),_c('input',{ref:"input",attrs:{"type":"file","multiple":_vm.multiple,"accept":_vm.accept},on:{"change":function($event){return _vm.changed($event.target.files)}}})],1),(_vm.hint)?_c('div',{staticClass:"control-hint"},[_c('p',[_vm._v(_vm._s(_vm.hint))])]):_vm._e()])}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "FileDropper.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__